import { FieldType } from '../types';

// A single mappable form field, flattened out of any enclosing section(s).
export interface MappableField {
    name: string;
    /**
     * What the dropdown shows: the field's own label, prefixed with the
     * section and step it sits in *only where that prefix distinguishes it*
     * from another field with the same label. See the note in the flattener.
     */
    label: string;
    ownLabel: string; // the field's own label without section prefix, for auto-detect matching
    /** Section and step labels above this field, outermost first. Empty at the top level. */
    sectionPath: string[];
    type: FieldType | string;
}

// Options controlling how fields are flattened for a mapping dropdown.
export interface FlattenMappableFieldsOptions {
    excludeTypes?: Iterable<string>;
    sectionSeparator?: string;
}

/**
 * Recursively flattens form fields, descending into sections and steps.
 *
 * The path is carried down but only spent where it buys something. It used to
 * be prefixed onto every nested field unconditionally, and on a multi-step form
 * that is every field in the form: twenty-seven options all beginning
 * "Anmeldung › ", in a select whose trigger truncates, so the mapping read as
 * one repeated section title with the actual field name cut off the end. The
 * prefix existed for the case it is kept for here — two fields that really do
 * share a label, in two branches of a conditional section, where the branch
 * name is the only thing telling "Mobil-Nummer" from "Mobil-Nummer".
 *
 * The step label joins the section label in that prefix, because the step is
 * the level that actually differs; the section above it is the same for both
 * halves of the pair being told apart.
 */
export const flattenMappableFields = (
    fields: any[],
    { excludeTypes = [], sectionSeparator = ' › ' }: FlattenMappableFieldsOptions = {}
): MappableField[] => {
    const excluded = new Set(excludeTypes);

    // Walks one level of fields, carrying the accumulated section-label prefix.
    const walk = (items: any[], prefix: string[]): MappableField[] =>
        (items ?? []).flatMap((field) => {
            // Sections hold no storable value themselves — recurse into their children.
            if (field?.type === 'section') {
                return (field.children ?? []).flatMap((child: any) =>
                    walk(child?.fields ?? [], [...prefix, field.label, child?.label].filter(Boolean))
                );
            }

            // Skip nameless fields and any caller-excluded types.
            if (!field?.name || excluded.has(field?.type)) return [];

            const ownLabel = field.label || field.name;

            return [{ name: field.name, label: ownLabel, ownLabel, sectionPath: prefix, type: field.type }];
        });

    const flat = walk(fields, []);

    // How often each label occurs, which is what decides whether its path is
    // worth the width. Compared case-insensitively and trimmed: two branches of
    // one form are written by the same person on two different days.
    const key = (label: string) => label.replace(/\s+/g, ' ').trim().toLowerCase();
    const occurrences = new Map<string, number>();

    flat.forEach((field) => occurrences.set(key(field.ownLabel), (occurrences.get(key(field.ownLabel)) ?? 0) + 1));

    return flat.map((field) =>
        (occurrences.get(key(field.ownLabel)) ?? 0) > 1 && field.sectionPath.length > 0
            ? { ...field, label: [...field.sectionPath, field.ownLabel].join(sectionSeparator) }
            : field
    );
};
